// src/pages/products/ImportProducts.jsx
// Excel import wizard: template download → upload → validation preview → bulk create.
// Auto-creates missing categories. Skips barcodes that already exist.
import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useQueryClient } from '@tanstack/react-query';
import api from '../../config/api';
import { exportStyledExcel } from '../../utils/exportExcel';

const ACCENT = '#2e9f86';

// flexible header matching (EN + AR)
const HEADER_ALIASES = {
  name:     ['name', 'product', 'product name', 'الاسم', 'اسم', 'اسم المنتج'],
  name_ar:  ['arabic name', 'name_ar', 'name ar', 'الاسم العربي', 'الاسم بالعربي'],
  barcode:  ['barcode', 'بار كود', 'باركود'],
  sku:      ['sku', 'code', 'رمز'],
  category: ['category', 'الفئة', 'فئة', 'القسم'],
  cost:     ['cost', 'cost (usd)', 'cost usd', 'الكلفة', 'كلفة'],
  price:    ['price', 'price (usd)', 'price usd', 'price_usd', 'السعر', 'سعر'],
  stock:    ['stock', 'qty', 'quantity', 'المخزون', 'الكمية', 'كمية'],
  unit:     ['unit', 'الوحدة', 'وحدة'],
};

const matchHeader = (h) => {
  const n = String(h || '').toLowerCase().trim();
  for (const [key, aliases] of Object.entries(HEADER_ALIASES)) {
    if (aliases.includes(n)) return key;
  }
  return null;
};

export default function ImportProducts({ onClose, products, categories, rate }) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState('pick'); // pick | preview | importing | done
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const existingBarcodes = new Set(products.map(p => String(p.barcode || '').trim()).filter(Boolean));
  const existingNames = new Set(products.map(p => String(p.name || '').trim().toLowerCase()));
  const catByName = {};
  categories.forEach(c => { catByName[String(c.name || '').trim().toLowerCase()] = c._id; });

  const downloadTemplate = () => exportStyledExcel({
    filename: 'mmm-pos-products-template',
    sheetName: 'Products',
    title: 'MMM POS — Products Import Template',
    subtitle: 'Fill one product per row. Only Name and Price are required. Delete the example rows before importing.',
    columns: [
      { header: 'Name', key: 'name', width: 26 },
      { header: 'Arabic Name', key: 'name_ar', width: 20 },
      { header: 'Barcode', key: 'barcode', width: 16 },
      { header: 'SKU', key: 'sku', width: 12 },
      { header: 'Category', key: 'category', width: 16 },
      { header: 'Cost (USD)', key: 'cost', width: 12, align: 'right' },
      { header: 'Price (USD)', key: 'price', width: 12, align: 'right' },
      { header: 'Stock', key: 'stock', width: 10, align: 'right' },
      { header: 'Unit', key: 'unit', width: 10 },
    ],
    rows: [
      { name: 'Pepsi 330ml', name_ar: 'بيبسي', barcode: '5449000000996', sku: '', category: 'Drinks', cost: 0.4, price: 0.75, stock: 48, unit: 'piece' },
      { name: 'Lays Chips Family', name_ar: 'شيبس ليز', barcode: '', sku: '', category: 'Snacks', cost: 1.1, price: 1.75, stock: 24, unit: 'piece' },
    ],
  });

  const handleFile = async (file) => {
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      // find the header row (first row containing a recognizable "name" header)
      let headerIdx = raw.findIndex(r => r.some(c => matchHeader(c) === 'name'));
      if (headerIdx === -1) { alert('Could not find a "Name" column. Use the template.'); return; }
      const headerMap = raw[headerIdx].map(matchHeader);
      const seenBarcodes = new Set();
      const seenNames = new Set();
      const parsed = [];
      for (let i = headerIdx + 1; i < raw.length; i++) {
        const line = raw[i];
        if (!line || line.every(c => String(c).trim() === '')) continue;
        const r = {};
        headerMap.forEach((key, ci) => { if (key) r[key] = String(line[ci] ?? '').trim(); });
        const name = r.name || '';
        const price = Number(r.price);
        const barcode = r.barcode || '';
        const errors = [];
        if (!name) errors.push('missing name');
        if (r.price !== '' && (isNaN(price) || price < 0)) errors.push('bad price');
        if (barcode && existingBarcodes.has(barcode)) errors.push('barcode already in system');
        if (barcode && seenBarcodes.has(barcode)) errors.push('duplicate barcode in file');
        if (name && (existingNames.has(name.toLowerCase()) || seenNames.has(name.toLowerCase()))) errors.push('name already exists');
        if (barcode) seenBarcodes.add(barcode);
        if (name) seenNames.add(name.toLowerCase());
        parsed.push({
          name, name_ar: r.name_ar || '', barcode, sku: r.sku || '',
          category: r.category || '', cost: Number(r.cost) || 0,
          price: isNaN(price) ? 0 : price, stock: Number(r.stock) || 0,
          unit: r.unit || 'piece',
          errors, skip: errors.length > 0,
        });
      }
      if (!parsed.length) { alert('No data rows found in the file.'); return; }
      setRows(parsed);
      setStep('preview');
    } catch {
      alert('Could not read this file. Save it as .xlsx or .csv and try again.');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const runImport = async () => {
    const valid = rows.filter(r => !r.skip);
    setStep('importing');
    setProgress({ done: 0, total: valid.length });

    // 1) create missing categories
    const catMap = { ...catByName };
    const newCats = [...new Set(valid.map(r => r.category.trim()).filter(c => c && !catMap[c.toLowerCase()]))];
    for (const cname of newCats) {
      try {
        const res = await api.post('/api/categories', { name: cname });
        catMap[cname.toLowerCase()] = res.data._id || res.data.data?._id;
      } catch { /* category may exist already */ }
    }

    // 2) create products one by one (shows real progress, partial success is fine)
    let created = 0; const failed = [];
    for (let i = 0; i < valid.length; i++) {
      const r = valid[i];
      try {
        await api.post('/api/products', {
          name: r.name, name_ar: r.name_ar, type: 'simple',
          barcode: r.barcode, sku: r.sku,
          category_id: r.category ? (catMap[r.category.toLowerCase()] || null) : null,
          cost: r.cost, cost_currency: 'USD',
          price_usd: r.price, price_lbp: Math.round(r.price * (rate || 89500)),
          stock: r.stock, manage_stock: true, unit: r.unit,
        });
        created++;
      } catch (err) {
        failed.push({ name: r.name, error: err.response?.data?.error || 'error' });
      }
      setProgress({ done: i + 1, total: valid.length });
    }

    queryClient.invalidateQueries(['products']);
    queryClient.invalidateQueries(['categories']);
    setResult({ created, failed, skipped: rows.length - valid.length, newCats: newCats.length });
    setStep('done');
  };

  const validCount = rows.filter(r => !r.skip).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-[760px] max-w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-bold text-lg text-gray-800">📥 Import Products from Excel</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {step === 'pick' && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                <p className="font-semibold text-gray-800 mb-1">1 — Get the template</p>
                <p className="text-sm text-gray-500 mb-3">Or use any Excel/CSV that has at least a <b>Name</b> column (Arabic headers work too). Price, barcode, category, cost and stock are optional.</p>
                <button onClick={downloadTemplate} className="px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white text-sm font-medium rounded-lg">⬇ Download Template</button>
              </div>
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                <p className="font-semibold text-gray-800 mb-1">2 — Upload your file</p>
                <p className="text-sm text-gray-500 mb-3">.xlsx or .csv — you'll see a preview before anything is created.</p>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={e => handleFile(e.target.files?.[0])}
                  className="block text-sm text-gray-600 file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:text-white file:font-semibold file:cursor-pointer" style={{ colorScheme: 'light' }} />
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div>
              <div className="flex items-center gap-3 mb-3 text-sm flex-wrap">
                <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold">{validCount} ready ✓</span>
                {rows.length - validCount > 0 && <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 font-bold">{rows.length - validCount} will be skipped</span>}
              </div>
              <div className="border border-gray-200 rounded-lg overflow-auto" style={{ maxHeight: 380 }}>
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      {['', 'Name', 'Barcode', 'Category', 'Cost', 'Price', 'Stock', 'Issue'].map(h => <th key={h} className="text-left px-2 py-2 font-semibold text-gray-500">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((r, i) => (
                      <tr key={i} className={r.skip ? 'bg-red-50/60' : ''}>
                        <td className="px-2 py-1.5">{r.skip ? '⛔' : '✓'}</td>
                        <td className="px-2 py-1.5 font-medium">{r.name || <i className="text-gray-400">—</i>}{r.name_ar ? <span className="text-gray-400"> · {r.name_ar}</span> : ''}</td>
                        <td className="px-2 py-1.5 font-mono">{r.barcode || '—'}</td>
                        <td className="px-2 py-1.5">{r.category || '—'}{r.category && !catByName[r.category.toLowerCase()] && <span className="ml-1 text-blue-600 font-bold">(new)</span>}</td>
                        <td className="px-2 py-1.5 text-right">{r.cost || ''}</td>
                        <td className="px-2 py-1.5 text-right font-semibold">{r.price}</td>
                        <td className="px-2 py-1.5 text-right">{r.stock || ''}</td>
                        <td className="px-2 py-1.5 text-red-600">{r.errors.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {step === 'importing' && (
            <div className="py-12 text-center">
              <div className="text-3xl mb-3">⏳</div>
              <p className="font-semibold text-gray-800 mb-3">Importing {progress.done} / {progress.total}…</p>
              <div className="w-72 mx-auto bg-gray-100 rounded-full h-3 overflow-hidden">
                <div className="h-3 rounded-full transition-all" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`, background: ACCENT }} />
              </div>
              <p className="text-xs text-gray-400 mt-3">Don't close this window.</p>
            </div>
          )}

          {step === 'done' && result && (
            <div className="py-8 text-center">
              <div className="text-4xl mb-3">{result.failed.length === 0 ? '🎉' : '⚠️'}</div>
              <p className="font-bold text-lg text-gray-800 mb-1">{result.created} products created</p>
              <p className="text-sm text-gray-500">
                {result.newCats > 0 && <>{result.newCats} new categories · </>}
                {result.skipped > 0 && <>{result.skipped} rows skipped · </>}
                {result.failed.length > 0 && <span className="text-red-600">{result.failed.length} failed</span>}
              </p>
              {result.failed.length > 0 && (
                <div className="mt-3 text-left max-w-sm mx-auto text-xs text-red-600 bg-red-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                  {result.failed.map((f, i) => <div key={i}>• {f.name}: {f.error}</div>)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-2 px-5 py-4 border-t">
          {step === 'preview' && (
            <>
              <button onClick={() => { setRows([]); setStep('pick'); }} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg">← Back</button>
              <button onClick={runImport} disabled={validCount === 0}
                className="flex-1 px-4 py-2 text-white text-sm font-bold rounded-lg disabled:opacity-40" style={{ background: ACCENT }}>
                Import {validCount} products
              </button>
            </>
          )}
          {(step === 'pick' || step === 'done') && (
            <button onClick={onClose} className="ml-auto px-5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg">Close</button>
          )}
        </div>
      </div>
    </div>
  );
}
